// ─── Free-form ticket canvas: the data model ────────────────────────────────
//
// A free-form template stores every printable thing as one absolutely placed
// box on the paper:
//
//   { id, type, side, x, y, w, h, rotation, z, locked, hidden, props, style }
//
// Geometry is in MILLIMETRES from the top-left corner of the paper (not the
// printable area), so the studio preview, the printed page and every screen
// zoom agree exactly. `props` is type-specific; `style` is shared by all types.
//
// Dynamic content (a long ornaments list, long terms) never moves anything
// else: it shrinks inside its own box down to MIN_FIT_SCALE (see FitBox).

export const PX_PER_MM = 96 / 25.4; // CSS px in one mm (3.7795…)
export const MIN_FIT_SCALE = 0.6;
export const CANVAS_VERSION = 1;

export const PAPER_MM = {
  A4: [210, 297],
  A5: [148, 210],
};

export const paperDims = (paperSize, orientation) => {
  const [w, h] = PAPER_MM[paperSize] || PAPER_MM.A4;
  return orientation === "landscape" ? [h, w] : [w, h];
};

export const round1 = (n) => Math.round(n * 10) / 10;

export const newElementId = () =>
  `el_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export const DEFAULT_STYLE = {
  fontSize: 9, // pt
  fontWeight: 400,
  fontStyle: "normal",
  color: "#111827",
  align: "left", // left | center | right
  vAlign: "top", // top | middle | bottom
  bg: "",
  borderWidth: 0, // px
  borderColor: "#94A3B8",
  borderStyle: "solid",
  radius: 0, // mm
  padding: 0.5, // mm
  opacity: 1,
  lineHeight: 1.3,
  fontFamily: "",
};

// ─── Data fields ─────────────────────────────────────────────────────────────
// Each field reads one value out of the ticket context the generator builds
// (see buildCanvasContext in PawnTicketGenerator). `get` returns a display
// string; an empty string means "nothing to print".
const inr = (n) => {
  const v = Number(n) || 0;
  return `₹${v.toLocaleString("en-IN")}`;
};
const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString("en-IN");
};
const wt = (n) => `${(Number(n) || 0).toFixed(3)} g`;

export const FIELD_GROUPS = [
  "Store",
  "Ticket",
  "Customer",
  "Loan",
  "Payment",
  "Ornaments",
  "Receipt",
];

export const FIELD_DEFS = [
  // Store
  { key: "storeName", group: "Store", label: "Store Name", get: (c) => c.store.name || "" },
  { key: "storeAddress", group: "Store", label: "Address", get: (c) => c.store.address || "" },
  { key: "storePhone", group: "Store", label: "Phone", get: (c) => c.store.phone || "" },
  { key: "storeEmail", group: "Store", label: "Email", get: (c) => c.store.email || "" },
  { key: "storeGst", group: "Store", label: "GSTIN", get: (c) => c.store.gst || "" },
  { key: "storePblId", group: "Store", label: "PBL No", get: (c) => c.store.pblId || "" },
  // Ticket
  { key: "voucherNo", group: "Ticket", label: "Voucher No", get: (c) => c.ticket.voucherNo || "" },
  { key: "ticketDate", group: "Ticket", label: "Date", get: (c) => fmtDate(c.ticket.date) },
  { key: "ticketTime", group: "Ticket", label: "Time", get: (c) => c.ticket.time || "" },
  { key: "ticketType", group: "Ticket", label: "Ticket Type", get: (c) => c.ticketTypeLabel || "" },
  // Customer
  { key: "customerName", group: "Customer", label: "Name", get: (c) => c.customer.name || "" },
  { key: "customerSonOf", group: "Customer", label: "S/o, W/o, D/o", get: (c) => c.customer.sonOf || "" },
  { key: "customerMobile", group: "Customer", label: "Mobile", get: (c) => c.customer.mobile || "" },
  { key: "customerAddress", group: "Customer", label: "Address", get: (c) => c.customer.address || "" },
  { key: "customerAadhar", group: "Customer", label: "Aadhar", get: (c) => c.customer.aadhar || "" },
  { key: "customerPan", group: "Customer", label: "PAN", get: (c) => c.customer.pan || "" },
  { key: "customerLedgerNo", group: "Customer", label: "Ledger No", get: (c) => c.customer.ledgerNo || "" },
  // Loan
  { key: "loanAmount", group: "Loan", label: "Loan Amount", get: (c) => inr(c.summary.totalLoanAmount || c.loan.principal) },
  { key: "loanAmountWords", group: "Loan", label: "Amount in Words", get: (c) => c.words(c.summary.totalLoanAmount || c.loan.principal) },
  { key: "itemValue", group: "Loan", label: "Item Value", get: (c) => inr(c.summary.totalOrnamentValue || c.loan.itemValue) },
  // Stored monthly; printed per annum (1.16 → 13.92% p.a.).
  { key: "interestRate", group: "Loan", label: "Interest Rate", get: (c) => (Number(c.loan.interestRate) ? `${Number((Number(c.loan.interestRate) * 12).toFixed(2))}% per annum` : "") },
  { key: "interestAmount", group: "Loan", label: "Interest Amount", get: (c) => inr(c.summary.totalInterestAmount || c.loan.interestAmount) },
  { key: "scheme", group: "Loan", label: "Scheme", get: (c) => c.loan.scheme || "" },
  { key: "insuranceAmount", group: "Loan", label: "Insurance", get: (c) => (c.loan.insuranceAmount ? inr(c.loan.insuranceAmount) : "") },
  { key: "collectByDate", group: "Loan", label: "Collect By", get: (c) => fmtDate(c.loan.collectByDate) },
  { key: "remarks", group: "Loan", label: "Remarks", get: (c) => c.loan.remarks || "" },
  // Payment
  { key: "paymentAmount", group: "Payment", label: "Amount Paid", get: (c) => inr(c.payment.paymentAmount) },
  { key: "paymentMethod", group: "Payment", label: "Payment Mode", get: (c) => c.payment.paymentMethod || "" },
  { key: "cashAmount", group: "Payment", label: "Cash", get: (c) => (c.payment.cashAmount ? inr(c.payment.cashAmount) : "") },
  { key: "bankAmount", group: "Payment", label: "Bank", get: (c) => (c.payment.bankAmount ? inr(c.payment.bankAmount) : "") },
  // Ornaments
  { key: "itemCount", group: "Ornaments", label: "No. of Items", get: (c) => String(c.items.length || "") },
  { key: "totalPcs", group: "Ornaments", label: "Total Pcs", get: (c) => String(c.items.reduce((s, i) => s + (Number(i.pcs) || 0), 0) || "") },
  { key: "totalGrossWeight", group: "Ornaments", label: "Gross Wt", get: (c) => wt(c.items.reduce((s, i) => s + (Number(i.grossWeight) || 0), 0)) },
  { key: "totalNetWeight", group: "Ornaments", label: "Net Wt", get: (c) => wt(c.items.reduce((s, i) => s + (Number(i.weight) || 0), 0)) },
  { key: "itemsList", group: "Ornaments", label: "Items", get: (c) => c.items.map((i) => i.description).filter(Boolean).join(", ") },
  // Receipt / settlement
  { key: "interestPaid", group: "Receipt", label: "Interest Paid", get: (c) => (c.receipt.interestPaid != null ? inr(c.receipt.interestPaid) : "") },
  { key: "interestDiscount", group: "Receipt", label: "Interest Discount", get: (c) => (Number(c.receipt.interestDiscount) ? inr(c.receipt.interestDiscount) : "") },
  { key: "insurancePaid", group: "Receipt", label: "Insurance Paid", get: (c) => (Number(c.receipt.insurancePaid) ? inr(c.receipt.insurancePaid) : "") },
  { key: "otherCharges", group: "Receipt", label: "Other Charges", get: (c) => (Number(c.receipt.otherCharges) ? inr(c.receipt.otherCharges) : "") },
  { key: "principalPaid", group: "Receipt", label: "Principal Paid", get: (c) => (c.receipt.principalPaid != null ? inr(c.receipt.principalPaid) : "") },
  { key: "totalPaid", group: "Receipt", label: "Total Paid", get: (c) => (c.receipt.totalPaid != null ? inr(c.receipt.totalPaid) : "") },
  { key: "totalPaidWords", group: "Receipt", label: "Total in Words", get: (c) => (c.receipt.totalPaid != null ? c.words(c.receipt.totalPaid) : "") },
  { key: "loanVoucherNo", group: "Receipt", label: "Against Loan", get: (c) => c.receipt.loanVoucherNo || "" },
  { key: "balanceAfter", group: "Receipt", label: "Balance Principal", get: (c) => (c.receipt.balanceAfter != null ? inr(c.receipt.balanceAfter) : "") },
  { key: "interestPaidTill", group: "Receipt", label: "Interest Paid Till", get: (c) => fmtDate(c.receipt.interestPaidTill) },
];

export const FIELD_BY_KEY = Object.fromEntries(FIELD_DEFS.map((f) => [f.key, f]));

export const fieldValue = (key, ctx) => {
  const def = FIELD_BY_KEY[key];
  if (!def || !ctx) return "";
  try {
    return def.get(ctx) ?? "";
  } catch (e) {
    return "";
  }
};

// Free text may embed any field as {fieldKey}, e.g.
// "Received from {customerName} the sum of {totalPaid}". Unknown keys are left
// as typed so a typo is visible on the preview rather than silently vanishing.
export const fillTokens = (text, ctx) =>
  String(text || "").replace(/\{(\w+)\}/g, (m, key) =>
    FIELD_BY_KEY[key] ? fieldValue(key, ctx) : m,
  );

// ─── Composite blocks ────────────────────────────────────────────────────────
// Whole sections of the classic ticket, placed as one box. They render with the
// same code as the flow layout, so a table or the terms look identical.
export const BLOCK_DEFS = [
  { id: "items", label: "Ornaments Table" },
  { id: "receiptSummary", label: "Receipt Amounts" },
  { id: "terms", label: "Terms & Conditions" },
  { id: "signatures", label: "Signature Lines" },
  { id: "customer", label: "Customer Details (block)" },
  { id: "loan", label: "Loan Details (block)" },
  { id: "payment", label: "Payment Details (block)" },
  { id: "store", label: "Store Header (block)" },
  { id: "header", label: "Heading / Invocation" },
  { id: "footer", label: "Footer Note" },
];
export const BLOCK_LABEL = Object.fromEntries(BLOCK_DEFS.map((b) => [b.id, b.label]));

// ─── Element types ───────────────────────────────────────────────────────────
// `make` returns the type-specific part of a fresh element; w/h are mm.
export const ELEMENT_TYPES = {
  field: {
    label: "Data Field",
    make: (opts = {}) => ({
      w: 70,
      h: 6,
      props: { key: opts.key || "customerName", showLabel: true, label: "", labelBold: true },
    }),
  },
  text: {
    label: "Text",
    make: () => ({ w: 60, h: 8, props: { text: "Double-click to edit text" } }),
  },
  qr: {
    label: "QR Code",
    keepRatio: true,
    make: () => ({ w: 28, h: 28, props: {} }),
  },
  logo: {
    label: "Store Logo",
    make: () => ({ w: 30, h: 20, props: { fit: "contain" } }),
  },
  photo: {
    label: "Photo",
    make: (opts = {}) => ({
      w: 25,
      h: 25,
      props: { which: opts.which || "customer", fit: "cover" },
      style: { borderWidth: 1 },
    }),
  },
  image: {
    label: "Image",
    make: () => ({ w: 30, h: 20, props: { src: "", fit: "contain" } }),
  },
  line: {
    label: "Line",
    make: (opts = {}) =>
      opts.vertical
        ? { w: 1, h: 40, props: { direction: "v", thickness: 1, dash: "solid" }, style: { color: "#94A3B8" } }
        : { w: 80, h: 1, props: { direction: "h", thickness: 1, dash: "solid" }, style: { color: "#94A3B8" } },
  },
  rect: {
    label: "Box",
    make: () => ({ w: 60, h: 30, props: {}, style: { borderWidth: 1, radius: 1 } }),
  },
  block: {
    label: "Block",
    make: (opts = {}) => {
      const sizes = {
        items: [190, 45],
        receiptSummary: [120, 60],
        terms: [190, 80],
        signatures: [190, 18],
        customer: [190, 45],
        loan: [190, 25],
        payment: [190, 20],
        store: [190, 40],
        header: [190, 8],
        footer: [190, 8],
      };
      const [w, h] = sizes[opts.section] || [120, 30];
      return { w, h, props: { section: opts.section || "items" } };
    },
  },
  copyLabel: {
    label: "Copy Caption",
    make: () => ({
      w: 34,
      h: 5,
      props: {},
      style: {
        fontSize: 7,
        fontWeight: 700,
        align: "center",
        vAlign: "middle",
        borderWidth: 1,
        radius: 0.8,
        color: "#334155",
      },
    }),
  },
};

// Palette shown in the studio. Each entry is one "add" action.
export const PALETTE = [
  { group: "Basics", items: [
    { type: "text", label: "Text" },
    { type: "line", label: "Horizontal Line" },
    { type: "line", label: "Vertical Line", opts: { vertical: true } },
    { type: "rect", label: "Box / Frame" },
    { type: "image", label: "Upload Image" },
  ] },
  { group: "Ticket", items: [
    { type: "qr", label: "QR Code" },
    { type: "logo", label: "Store Logo" },
    { type: "photo", label: "Customer Photo", opts: { which: "customer" } },
    { type: "photo", label: "Ornament Photo", opts: { which: "ornament" } },
    { type: "copyLabel", label: "Copy Caption" },
  ] },
  { group: "Blocks", items: BLOCK_DEFS.map((b) => ({ type: "block", label: b.label, opts: { section: b.id } })) },
];

export const createElement = (type, opts = {}, at = {}) => {
  const def = ELEMENT_TYPES[type];
  const base = def.make(opts);
  return {
    id: newElementId(),
    type,
    side: at.side || "front",
    x: round1(at.x ?? 10),
    y: round1(at.y ?? 10),
    w: base.w,
    h: base.h,
    rotation: 0,
    z: at.z ?? 1,
    locked: false,
    hidden: false,
    props: base.props || {},
    style: { ...DEFAULT_STYLE, ...(base.style || {}) },
  };
};

export const elementTitle = (el) => {
  if (!el) return "";
  if (el.type === "field") return FIELD_BY_KEY[el.props?.key]?.label || "Field";
  if (el.type === "block") return BLOCK_LABEL[el.props?.section] || "Block";
  if (el.type === "photo") return el.props?.which === "ornament" ? "Ornament Photo" : "Customer Photo";
  if (el.type === "text") return (el.props?.text || "Text").slice(0, 24);
  return ELEMENT_TYPES[el.type]?.label || el.type;
};

// Smallest box (mm) an element may be resized to. Without a floor a handle
// dragged past the opposite edge collapses the box to a dot: its content
// shrinks to nothing and it can no longer be grabbed. A line keeps its thin
// axis free (that is the line's thickness box) but not its length.
export const minSizeFor = (el) => {
  switch (el?.type) {
    case "block":
      return [20, 10];
    case "qr":
      return [12, 12];
    case "line":
      return el.props?.direction === "v" ? [0.3, 3] : [3, 0.3];
    case "photo":
    case "logo":
    case "image":
      return [5, 5];
    case "rect":
      return [3, 3];
    default:
      return [8, 3]; // field, text, copy caption
  }
};

// Keep a box usable and on the paper: at least its minimum size, no bigger
// than the page, and fully on the page.
export const clampToPage = (el, pageW, pageH) => {
  const [mw, mh] = minSizeFor(el);
  const w = round1(Math.min(Math.max(el.w, mw), pageW));
  const h = round1(Math.min(Math.max(el.h, mh), pageH));
  return {
    ...el,
    w,
    h,
    x: round1(Math.min(Math.max(el.x, 0), pageW - w)),
    y: round1(Math.min(Math.max(el.y, 0), pageH - h)),
  };
};

// Fill in anything a stored element might be missing (older versions, hand
// edits) so the renderer never has to null-check.
export const normalizeElements = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((el) => el && ELEMENT_TYPES[el.type])
    .map((el, i) => ({
      id: el.id || newElementId(),
      type: el.type,
      side: el.side === "back" ? "back" : "front",
      x: Number(el.x) || 0,
      y: Number(el.y) || 0,
      w: Math.max(1, Number(el.w) || 10),
      h: Math.max(0.3, Number(el.h) || 5),
      rotation: Number(el.rotation) || 0,
      z: Number.isFinite(el.z) ? el.z : i + 1,
      locked: !!el.locked,
      hidden: !!el.hidden,
      props: { ...(el.props || {}) },
      style: { ...DEFAULT_STYLE, ...(el.style || {}) },
    }))
    // Repair boxes saved collapsed before minimum sizes existed.
    .map((el) => {
      const [mw, mh] = minSizeFor(el);
      return el.w >= mw && el.h >= mh
        ? el
        : { ...el, w: Math.max(el.w, mw), h: Math.max(el.h, mh) };
    });

// Fit a whole layout to a new ticket size (paper changed, or two copies per
// sheet halved it). Positions and boxes stretch with the page; text and
// pictures scale by the smaller factor so nothing distorts — a square QR stays
// square, a photo keeps its shape.
const MEDIA = new Set(["qr", "photo", "logo", "image"]);
export const rescaleElements = (list, [fw, fh], [tw, th]) => {
  if (!fw || !fh || (Math.abs(fw - tw) < 0.05 && Math.abs(fh - th) < 0.05)) return list;
  const sx = tw / fw;
  const sy = th / fh;
  const sf = Math.min(sx, sy);
  return list.map((el) => {
    const media = MEDIA.has(el.type);
    const w = media ? el.w * sf : el.w * sx;
    const h = media ? el.h * sf : el.h * sy;
    return {
      ...el,
      x: round1(el.x * sx),
      y: round1(el.y * sy),
      w: Math.max(0.5, round1(w)),
      h: Math.max(0.3, round1(h)),
      style: { ...el.style, fontSize: round1(Math.max(4, (el.style?.fontSize || 9) * sf)) },
    };
  });
};

// ─── Starter layout ──────────────────────────────────────────────────────────
// Builds a canvas that mirrors the classic stacked ticket, so "Switch to
// free-form" starts from something familiar instead of a blank page. Laid out
// on a 190mm-wide content column and scaled to the chosen paper.
export const seedCanvasLayout = ({ paperSize, orientation, paperW, paperH, margin = 10, isReceipt, sections = [], showHeaderMessage, showFooterMessage, printTwoCopies }) => {
  // paperW/paperH (mm) win when given — a half-sheet ticket (two copies side
  // by side) is not a standard paper size.
  const [pw, ph] = paperW && paperH ? [paperW, paperH] : paperDims(paperSize, orientation);
  // Small tickets get proportionally smaller margins and type.
  const m = Math.max(4, Math.min(Number(margin) || 10, pw * 0.06));
  const cw = pw - m * 2;
  const k = cw / 190; // horizontal scale relative to the A4 design
  const fk = Math.min(1, Math.max(0.6, k * 1.15)); // font scale, gentler than k
  const sideOf = (id) => sections.find((s) => s.id === id)?.side === "back" ? "back" : "front";
  const visible = (id) => {
    const s = sections.find((x) => x.id === id);
    return s ? s.visible !== false : true;
  };
  const out = [];
  let z = 1;
  // Layout is written in "design units": x across a 190-wide column (scaled by
  // k), y down an A4-like page (scaled by fk, so the vertical rhythm shrinks
  // with the type on a small ticket). On A4 both scales are 1 = plain mm.
  //   geo.abs   → y/h are real mm (bottom-anchored footer, back-side terms)
  //   geo.size  → a square picture of that many mm (QR, photos)
  //   geo.right → hug the right edge of the content column
  const Y = (y) => m + (y - m) * fk;
  const add = (type, opts, geo, extra = {}) => {
    const el = createElement(type, opts, { side: geo.side || "front", z: z++ });
    el.w = round1(geo.size ?? geo.w * k);
    el.h = round1(geo.size ?? (geo.abs ? geo.h : geo.h * fk));
    el.x = round1(geo.right ? pw - m - el.w : m + geo.x * k);
    el.y = round1(geo.abs ? geo.y : Y(geo.y));
    if (extra.props) el.props = { ...el.props, ...extra.props };
    if (extra.style) el.style = { ...el.style, ...extra.style };
    el.style.fontSize = round1((el.style.fontSize || 9) * fk);
    out.push(el);
    return el;
  };
  const field = (key, x, y, w, extra = {}) =>
    add("field", { key }, { x, y, w, h: extra.h || 5.5 }, extra);

  let y = m;
  if (printTwoCopies) {
    add("copyLabel", {}, { right: true, w: 34, abs: true, y: Math.max(2, m - 5.5), h: 4.5 * fk });
  }
  if (showHeaderMessage) {
    add("block", { section: "header" }, { x: 0, y, w: 190, h: 7 });
    y += 8;
  }
  // Store header: logo, name, contact, QR on the right.
  add("logo", {}, { x: 0, y, w: 28, h: 22 });
  field("storeName", 31, y, 120, { h: 8, props: { showLabel: false }, style: { fontSize: 15, fontWeight: 700, color: "#2563EB" } });
  field("storeAddress", 31, y + 8.5, 120, { h: 5, props: { showLabel: false } });
  field("storePhone", 31, y + 13.5, 60);
  field("storePblId", 92, y + 13.5, 59);
  // Square, right-aligned; never under 18mm so phones still scan it.
  add("qr", {}, { right: true, y, size: Math.max(18, 28 * fk) });
  y += 30;
  add("line", {}, { x: 0, y, w: 190, h: 1 });
  y += 2;
  // Ticket meta row.
  if (visible("ticketNo")) {
    field("voucherNo", 0, y, 62, { style: { fontWeight: 700 } });
    field(isReceipt ? "totalPaid" : "loanAmount", 64, y, 62, { style: { fontWeight: 700 } });
    field("ticketDate", 128, y, 36);
    field("ticketTime", 165, y, 25, { props: { showLabel: false } });
    y += 7;
    add("line", {}, { x: 0, y, w: 190, h: 1 });
    y += 2;
  }
  // Customer: fields on the left, photos on the right.
  if (visible("customer")) {
    const cy = y;
    field("customerName", 0, cy, 130, { style: { fontWeight: 700 } });
    field("customerSonOf", 0, cy + 6, 130);
    field("customerMobile", 0, cy + 12, 64);
    field("customerAadhar", 66, cy + 12, 64);
    field("customerAddress", 0, cy + 18, 130, { h: 10 });
    add("photo", { which: "customer" }, { x: 134, y: cy, size: 27 * Math.min(k, fk) });
    add("photo", { which: "ornament" }, { x: 163, y: cy, size: 27 * Math.min(k, fk) });
    y += 31;
  }
  if (visible("loan") && !isReceipt) {
    field("loanAmount", 0, y, 62, { style: { fontWeight: 800, fontSize: 10.5 } });
    field("interestRate", 64, y, 40);
    field("scheme", 106, y, 84);
    field("loanAmountWords", 0, y + 6, 190, { props: { showLabel: true } });
    y += 13;
  }
  if (visible("payment") && !isReceipt) {
    field("paymentMethod", 0, y, 110);
    field("paymentAmount", 112, y, 78);
    y += 7;
  }
  // The ornaments table / receipt money block — the type swap at render time
  // means one template works for any ticket type.
  const txH = isReceipt ? 62 : 55;
  add("block", { section: isReceipt ? "receiptSummary" : "items" }, { x: 0, y, w: 190, h: txH });
  y += txH + 2;
  if (visible("signatures")) {
    add("block", { section: "signatures" }, { x: 0, y, w: 190, h: 18 });
    y += 20;
  }
  const footerH = 7 * fk;
  if (visible("terms")) {
    const back = sideOf("terms") === "back";
    const ty = back ? m : Y(y);
    const th = back ? ph - m * 2 : Math.max(12, ph - m - ty - footerH - 2);
    add("block", { section: "terms" }, { x: 0, y: ty, w: 190, h: th, abs: true, side: back ? "back" : "front" });
  }
  if (showFooterMessage) {
    add("block", { section: "footer" }, { x: 0, y: ph - m - footerH, w: 190, h: footerH, abs: true });
  }
  return out;
};
